import { TimeScaleOptions, TimeScaleTickOptions } from "chart.js";
import dayjs from "dayjs";

import { getDateLabelByInterval, IntervalRange } from "components/ui/Chart/chart-interval.utils";
import { RequiredScalesChartConfiguration } from "components/ui/Chart/configurations/axes/axis.types";
import { KPITimeScale } from "components/ui/KPI/kpi.types";
import { Language, localeDateFNSMap } from "locales/languages.enum";

type XAxisConfiguration = RequiredScalesChartConfiguration<"line" | "bar">["options"]["scales"]["x"];

/**
 * @param options.offset - If true, extra space is added to the both edges and the axis is scaled to fit into the chart area.
 */

export const createTimeXAxis = (options?: {
    stacked?: boolean;
    locale?: Language;
    ticksSource?: TimeScaleTickOptions["source"];
    bounds?: TimeScaleOptions["bounds"];
    skipFirstTick?: boolean;
    axisLabel?: string;
    offset?: boolean;
    useISOWeek?: boolean;
    timeScale?: KPITimeScale;
    intervalRangeLabel?: IntervalRange;
}): XAxisConfiguration => {
    const configuration: XAxisConfiguration = {
        type: "time",
        adapters: {
            date: options?.locale
                ? {
                      locale: localeDateFNSMap[options.locale],
                  }
                : {},
        },
        time: {
            tooltipFormat: "MMM dd, yyyy",
            displayFormats: {
                day: "d MMM",
                month: "MMM yyyy",
            },
            minUnit: "day",
            isoWeekday: options?.useISOWeek,
        },
        ticks: {
            autoSkip: true,
            maxRotation: 0,
            // The source should stay set to `data` most of the times as Chart.js usually can't automatically determine the best tick values.
            // The most problematic charts are bar charts or charts which can receive daily, weekly and monthly data in the same data set.
            source: options?.ticksSource ?? "data",
            padding: 8,
            callback(tickValue) {
                if (options?.intervalRangeLabel) {
                    return getDateLabelByInterval(tickValue, options.intervalRangeLabel);
                }
                if (options?.timeScale === "month") {
                    return dayjs(tickValue).format("MMM");
                } else {
                    return dayjs(tickValue).format("D MMM");
                }
            },
        },
        grid: {
            // hide axis ticks outside of the chart
            tickLength: 0,
            display: false,
        },
        title: {
            display: options?.axisLabel ? true : false,
            text: options?.axisLabel,
        },
        stacked: options?.stacked ? true : false,
        bounds: options?.bounds ?? "data",
        afterUpdate(axis) {
            if (options?.skipFirstTick) {
                axis.ticks = axis.ticks.slice(1);
            }
        },
    };

    /**
     * If `true`, extra space is added to the both edges and the axis is scaled to fit into the chart area.
     * This is set to `false` for every chart except bar chart by default, for bar chart it defaults to `true`
     * Explicitly setting the offset to `undefined` is parsed as `false` by Chart.js instead
     */
    if (options?.offset === true) {
        configuration.offset = options.offset;
    }

    return configuration;
};
